import React, { FC, useCallback, useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import classNames from 'classnames'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { BasicCarousel } from '@/molecules/BasicCarousel'
import { LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import { VideoMetaData } from '@/services/ProjectsService'
import { GetVideosByPageForProjectWithIdQuery } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { isDefined } from '@/utils/primitives/generics'

interface VideoHorizontalScrollerProps {
  projectSlug: string
  videos: VideoMetaData[]
  padding?: boolean
}

const getVideoQuery = gql`
  query getVideosByPageForProjectWithId($projectSlug: String!, $page: String) {
    project(slug: $projectSlug) {
      id
      videos(page: $page) {
        guid
        title
        subtitle
        posterLandscapeCloudinaryPath
        watchPosition {
          position
        }
        source {
          duration
        }
        page
      }
    }
  }
`

export const VideoHorizontalScroller: FC<VideoHorizontalScrollerProps> = ({ videos, projectSlug, padding = false }) => {
  const track = useSafeTrack()

  const handleClick = useCallback(
    (video: VideoMetaData) => {
      track('Watch Project Bonus Video Clicked', { videoId: video.guid })
    },
    [track],
  )

  return (
    <BasicCarousel containerClassName={classNames({ 'px-4 sm:px-8 md:px-12 xl:px-16': padding })}>
      {videos?.map((video) => {
        let progress = 0

        if (video?.watchPosition?.position && video.source?.duration) {
          progress = (video?.watchPosition?.position / video?.source.duration) * 100
        }

        return (
          <div
            key={video.guid}
            className="w-2/3 min-w-[256px] max-w-[300px] shrink-0 snap-center snap-always sm:w-2/5 lg:w-3/5"
          >
            <LandscapeTileCard
              className="mr-4 h-full min-w-full"
              linkModel={{
                linkUrl: `/watch/${projectSlug}/videos/${video.page}/${video.guid}`,
                alt: video.title,
                imageUrl: video.posterLandscapeCloudinaryPath,
                label: video.title,
              }}
              onClick={() => handleClick(video)}
            >
              <PlayButtonThumbnailOverlay />
              <ProgressOverlay progress={progress} />
            </LandscapeTileCard>
            <TitleAndSubtitleDisplay className="my-1" subtitle={video.subtitle} />
          </div>
        )
      })}
    </BasicCarousel>
  )
}

export const useProjectVideos = (projectSlug: string, page: string) => {
  const [videos, setVideos] = useState<VideoMetaData[]>([])

  const { data, loading, refetch, error } = useQuery<GetVideosByPageForProjectWithIdQuery>(getVideoQuery, {
    variables: { projectSlug, page },
    errorPolicy: 'all',
  })

  useEffect(() => {
    setVideos((data?.project?.videos?.filter(isDefined) ?? []) as VideoMetaData[])
  }, [data])

  return {
    videos,
    loading,
    error,
    refetch,
  }
}
